import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import FiltersSection from '@/common/components/FiltersSection/FiltersSection'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import MetricsBreadcrumbs from '@/metrics/components/MetricsBreadcrumbs/MetricsBreadcrumbs'
import routes from '@/Router/routes.constants'

import CustomerNotesCard from './CustomerNotesCard/CustomerNotesCard'
import useCustomerNotesAnalysis from './CustomerNotesCard/hooks/useCustomerNotesAnalysis/useCustomerNotesAnalysis'
import useCustomerNotesExport from './CustomerNotesCard/hooks/useCustomerNotesExport/useCustomerNotesExport'
import useOpenTopicDrawer from './hooks/useOpenTopicDrawer/useOpenTopicDrawer'
import useReasonDetailSetup from './hooks/useReasonDetailSetup/useReasonDetailSetup'
import ReasonMetrics from './ReasonMetrics/ReasonMetrics'
import ReasonSummaryHeader from './ReasonSummaryHeader/ReasonSummaryHeader'
import TopicDrawer from './TopicDrawer/TopicDrawer'
import { unknownReasonKey } from './constants'
import { getIsReasonSummaryPending } from './helpers'
import * as Styles from './styles'

const ReasonMetricDetailPage = ({ isUnifiedDashboard }) => {
  const { reasonKey } = useParams()
  const { t } = useTranslation()

  const {
    info,
    regions,
    commonFilters,
    setCommonFilters,
    getUrlSearchParams,
    productsFilters,
    setProductsFilters,
    returnPortalId,
    salesOrdersDataActivated,
    showExchanges,
    subReasonsOrdering,
    reasonSummary,
    loadingReasonSummary,
    reasonSummaryError,
  } = useReasonDetailSetup({ reasonKey, isUnifiedDashboard })

  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })

  const storeId = info?.selectedStore?.id
  const showCustomerNotes =
    reasonKey !== unknownReasonKey && !!returnPortalFeatures?.return_notes_analysis_metrics_enabled

  const { data: notesAnalysis, refetch: refetchNotesAnalysis } = useCustomerNotesAnalysis({
    reasonId: reasonKey,
    storeId,
    filters: commonFilters,
    doRequest: showCustomerNotes,
  })
  const { exportToCsv: exportNotesCsv, exporting: exportingNotesCsv } = useCustomerNotesExport({
    reasonId: reasonKey,
    storeId,
    filters: commonFilters,
  })

  const { selectedTopic, show: showTopicDrawer, openTopic, closeTopic } = useOpenTopicDrawer()

  const getDashboardUrl = () => {
    return `${isUnifiedDashboard ? routes.merchantDashboardPage : routes.dashboardPage}?${getUrlSearchParams()}`
  }

  const getBreadcrumbs = () => {
    return [
      { title: t('Dashboard'), url: getDashboardUrl() },
      { title: t('Motivos') },
      {
        title: reasonSummary?.display || t('Motivo'),
        loading: getIsReasonSummaryPending({
          summary: reasonSummary,
          loading: loadingReasonSummary,
          error: reasonSummaryError,
        }),
      },
    ]
  }

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <MetricsBreadcrumbs breadcrumbs={getBreadcrumbs()} />
        <FiltersSection regions={regions} filters={commonFilters} onChange={setCommonFilters} />
      </Styles.HeaderContainer>
      <ReasonSummaryHeader
        summary={reasonSummary}
        loading={loadingReasonSummary}
        error={reasonSummaryError}
        showExchanges={showExchanges}
      />
      {showCustomerNotes && (
        <>
          <CustomerNotesCard
            data={notesAnalysis}
            refetch={refetchNotesAnalysis}
            onExport={exportNotesCsv}
            exporting={exportingNotesCsv}
            onClickTopic={openTopic}
          />
          <TopicDrawer
            key={selectedTopic?.topic_id}
            show={showTopicDrawer}
            selectedTopic={selectedTopic}
            onClose={closeTopic}
            reasonId={reasonKey}
            storeId={storeId}
            filters={commonFilters}
          />
        </>
      )}
      <ReasonMetrics
        reasonKey={reasonKey}
        returnPortalId={returnPortalId}
        commonFilters={commonFilters}
        salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
        isUnifiedDashboard={isUnifiedDashboard}
        showExchanges={showExchanges}
        subReasonsOrdering={subReasonsOrdering}
        productsFilters={productsFilters}
        setProductsFilters={setProductsFilters}
      />
    </Styles.Container>
  )
}

export default ReasonMetricDetailPage
